import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { TIMEFRAMES, effectiveLeverage } from "@strat/format";
import type { RiskParams, TradeDirection } from "@strat/format";

import { api } from "../api";
import { useSession } from "../session";
import { Alert, Field } from "../shell/ui";
import { BotLeverageFields, MarketTypeToggle, useBotLeverage } from "./BotLeverage";

/** Run a strategy as a hosted paper bot. Paper money, the hub's hardware, around the clock. */
export function DeployDialog({
  strategyId,
  strategyName,
  onClose,
  initial,
  risk,
  direction = "long",
}: {
  strategyId: string;
  strategyName: string;
  onClose: () => void;
  /** Settings to start from — the Workbench hands over the market it was testing. */
  initial?: {
    symbol?: string;
    timeframe?: string;
    marketType?: "spot" | "futures";
    startingCash?: number;
    note?: string;
  };
  /**
   * The strategy's risk settings. Leverage above 1× or funding starts the
   * dialog on futures; the bot's own leverage and funding are picked here.
   */
  risk?: RiskParams;
  direction?: TradeDirection;
}) {
  const { user } = useSession();
  const navigate = useNavigate();
  const lev = useBotLeverage(risk);
  const [marketType, setMarketType] = useState<"spot" | "futures">(
    initial?.marketType ?? (effectiveLeverage(risk ?? {}) > 1 || risk?.applyFunding ? "futures" : "spot"),
  );
  const symbols = useQuery({
    queryKey: ["hub-symbols", marketType],
    queryFn: () =>
      api<{ symbols: string[] }>(`/data/symbols?marketType=${marketType}`).then((r) => r.symbols),
  });
  const [symbol, setSymbol] = useState(initial?.symbol ?? "BTCUSDT");
  // Fewer coins have futures than spot: switching keeps the coin when it can.
  const list = symbols.data;
  useEffect(() => {
    if (list && list.length > 0 && !list.includes(symbol)) setSymbol(list[0]!);
  }, [list, symbol]);
  const [timeframe, setTimeframe] = useState<string>(
    initial?.timeframe && (TIMEFRAMES as readonly string[]).includes(initial.timeframe)
      ? initial.timeframe
      : "1h",
  );
  const [cash, setCash] = useState(
    Math.min(1_000_000, Math.max(100, Math.round(initial?.startingCash ?? 10_000))),
  );
  const [visibility, setVisibility] = useState<"public" | "private">("public");
  const deploy = useMutation({
    mutationFn: () =>
      api<{ id: string }>("/hub/bots", {
        method: "POST",
        body: {
          strategyId,
          symbol,
          timeframe,
          marketType,
          startingCash: cash,
          visibility,
          ...lev.overrides(marketType),
        },
      }),
    onSuccess: (r) => navigate(`/b/${r.id}`),
  });
  return (
    <div className="dialog-backdrop" role="presentation" onClick={onClose}>
      <div
        className="dialog card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="deploy-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card__body stack">
          <h2 id="deploy-title">Run “{strategyName}” as a paper bot</h2>
          <p className="muted small">
            It trades live prices with paper money, on StrategyHive's servers, until you stop it.{" "}
            {user?.role === "starter" ? "Starter accounts keep one bot; Pro keeps three." : ""}
          </p>
          <MarketTypeToggle value={marketType} onChange={setMarketType} />
          <div className="form-grid">
            <Field label="Market">
              <select value={symbol} onChange={(e) => setSymbol(e.target.value)}>
                {(symbols.data ?? ["BTCUSDT"]).map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </Field>
            <Field label="Timeframe">
              <select value={timeframe} onChange={(e) => setTimeframe(e.target.value)}>
                {TIMEFRAMES.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </Field>
            <Field label="Paper cash">
              <input
                type="number"
                min={100}
                max={1_000_000}
                step={100}
                value={cash}
                onChange={(e) => setCash(Number(e.target.value))}
              />
            </Field>
            <Field label="Visibility">
              <select
                value={visibility}
                onChange={(e) => setVisibility(e.target.value as "public" | "private")}
              >
                <option value="public">Public — in the public bot ranking, followable</option>
                <option value="private">Private — only you</option>
              </select>
            </Field>
          </div>
          <BotLeverageFields
            marketType={marketType}
            onMarketTypeChange={setMarketType}
            state={lev}
            direction={direction}
            margin={risk?.sizeMode === "pct_equity" ? (cash * risk.sizeValue) / 100 : null}
            stopLossPct={risk?.stopLossPct}
          />
          {initial?.note && <p className="small muted">{initial.note}</p>}
          {deploy.error && <Alert tone="error">{deploy.error.message}</Alert>}
          <div className="row row--end">
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--primary"
              disabled={deploy.isPending}
              onClick={() => deploy.mutate()}
            >
              {deploy.isPending ? "Starting…" : "Start paper bot"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
